import { z } from "zod";
import { GROUP_ROLE, groupRoleSchema } from "./groups.js";
import { REMOVE_MEMBER_CODE } from "./remove-member.js";

/**
 * Shared DTO cho Role Management — POST /v1/groups/:id/members/:mid/role
 * (US-E4.F6-0028 assign-admin / @UC0019, US-E4.F6-0029 assign-treasurer / @UC0020,
 *  US-E4.F6-0030 remove-role / @UC0019+@UC0020 — GỘP 1 endpoint, Phase 3 RBAC `@gate`).
 *
 * Một nguồn sự thật cho api (validate biên + shape response + mã lỗi) và mobile (gọi + map),
 * tránh drift (DRY). C5: schema validate tại biên tái dùng được.
 *
 * Cross-check API_BUILD_PLAN [đã xoá → TDD §26] Phase 3 (dòng 142): verb canonical = POST (DÙNG POST theo plan, dù vài
 * chỗ story ghi PATCH). OWNER-only (R-matrix §9, dòng 146 — Admin/Treasurer/Member KHÔNG đổi role).
 *
 * Logic theo `role` đích (1 endpoint xử lý cả 3 story):
 *  - role='admin'     → bổ nhiệm Admin (0028). Nhiều Admin OK (KHÔNG unique). Treasurer→Admin: slot
 *                       treasurer trống.
 *  - role='treasurer' → bổ nhiệm Treasurer (0029). CHÍNH XÁC 1 treasurer/nhóm — atomic swap: nếu đã
 *                       có treasurer Y khác, Y → member trong cùng transaction (enforce thêm bằng
 *                       partial unique index one_treasurer_per_group + catch P2002).
 *  - role='member'    → thu hồi quyền (0030). admin|treasurer X → member. KHÔNG xoá membership (X vẫn
 *                       active trong nhóm) — KHÁC hẳn DELETE member (0027).
 *
 * Bảo mật (C2/C3): target KHÔNG được gán 'owner' (one_owner invariant — đổi owner là transfer-owner,
 * endpoint khác). target='owner' → cấm (KHÔNG hạ/đổi role owner). KHÔNG tự đổi role chính mình
 * (owner không tự hạ mình gây mất owner). `.strict()` chặn field lạ tại biên.
 *
 * 🔴🔴 ROLE-CHANGE-BY-SEAT (review độc lập 12/10/2026, HIGH — cùng họ lỗi US-E2.F3-0870 của route xoá) — `:mid` =
 * **`GroupMember.id`** (id SUẤT). Trước đó route tra theo **userId** ⇒ `primaryUserId = X` trúng ghế đang GIỮ Ô CACHE của X:
 * phụ huynh P đại diện Bé A (giữ cột) và Bé B (cột NULL — unique `(groupId, primaryUserId)`), danh sách vẫn nêu `userId = P`
 * cho dòng Bé B ⇒ chủ nhóm đổi vai Bé B thì server đổi vai GHẾ BÉ A (Bé A là thủ quỹ ⇒ nhóm mất thủ quỹ lặng lẽ).
 *  - userId của client CŨ (flutter-web) còn được nhận làm đường DỰ PHÒNG, fail-closed: userId gắn ≥2 suất ⇒ 409
 *    `GROUP_MEMBER_ID_REQUIRED` (CÙNG mã với route xoá), 0 ghi.
 *  - Suất KHÔNG đứng tên chính (`primaryUserId` NULL) ⇒ 409 `GROUP_ROLE_SEAT_NOT_PRIMARY`, 0 ghi: guard vai đọc GHẾ CHÍNH
 *    CHỦ (`primaryUserId`), nên vai gán lên ghế phụ không trao quyền cho ai — gán vào đó là nói dối.
 *  - "Tự đổi vai mình" xét theo SUẤT (actor là `primaryUserId` của suất), không chỉ theo tham số.
 *  - `GET /groups/:id/members` trả cờ `roleAssignable` cho TỪNG dòng (server tính) để client không dựng nút dẫn vào lỗi chắc chắn.
 */

/**
 * Role được phép GÁN qua endpoint này (KHÔNG có 'owner' — owner chỉ đổi qua transfer-owner; đổi
 * owner ở đây sẽ phá one_owner invariant). Một nguồn sự thật cho request body.
 */
export const assignableRoleSchema = z.enum([
  GROUP_ROLE.ADMIN,
  GROUP_ROLE.TREASURER,
  GROUP_ROLE.MEMBER,
]);

export type AssignableRole = z.infer<typeof assignableRoleSchema>;

/**
 * Body cho POST /v1/groups/:id/members/:mid/role.
 *  - role BẮT BUỘC ∈ {admin, treasurer, member}; 'owner' → reject tại biên (C5 — chống nâng quyền).
 *  - `.strict()` chặn field lạ (chống over-post: KHÔNG nhận userId/status/id từ body — server quyết
 *    target từ path :mid, actor từ token).
 */
export const roleChangeRequestSchema = z
  .object({
    role: assignableRoleSchema,
  })
  .strict();

export type RoleChangeRequest = z.infer<typeof roleChangeRequestSchema>;

/**
 * Mã lỗi máy-đọc-được (RFC7807 problem+json `code`) cho role-management — ổn định qua i18n/đổi
 * wording. Client discriminate theo `code`, KHÔNG so khớp message.
 */
export const ROLE_CHANGE_CODE = {
  /** Cấm đổi role của Owner (one_owner invariant — dùng transfer-owner thay thế). */
  CANNOT_CHANGE_OWNER: "GROUP_ROLE_CANNOT_CHANGE_OWNER",
  /** Cấm tự đổi role chính mình (owner không tự hạ mình gây mất owner). */
  CANNOT_CHANGE_SELF: "GROUP_ROLE_CANNOT_CHANGE_SELF",
  /**
   * 🔴 US-IDM.F1-0288-S05 (@UC9503): cấm gán vai điều hành (treasurer) cho member chưa có tài khoản
   * người dùng (unclaimed / 0 MembershipAccount) — "thủ quỹ ma" không ai chịu trách nhiệm tiền.
   */
  MEMBER_UNCLAIMED: "GROUP_MEMBER_UNCLAIMED",
  /**
   * 🔴 FIX-1278 / FIX-1278b (QĐ PO 07/10/2026 lần 2 · FUND-DEBT-SELF-CONFIRM) — actor còn khoản "nợ với quỹ" MỞ (đợt `fund_debt` chưa khép)
   * hoặc NỢ SẮP CÓ (khoản chi khẩn cấp của mình đang chờ biểu quyết truy nhận) mà thao tác LÀM GIẢM số quản lý THẬT của nhóm (hạ vai O/A/T
   * xuống member · gỡ thành viên đang là O/A/T · chuyển quyền làm mất một quản lý) ⇒ 409, 0 ghi. Chặn lối "bớt người quản lý ⇒ thành quản lý
   * duy nhất ⇒ tự xác nhận khoản của mình". NÂNG người khác / đổi giữa các vai quản lý ⇒ được. Dùng CHUNG cho đổi vai và gỡ thành viên (cùng
   * một lý do, cùng một lối ra cho người dùng). Không `params`.
   */
  BLOCKED_FUND_DEBT: "ROLE_CHANGE_BLOCKED_FUND_DEBT",
  /**
   * 🔴 ROLE-CHANGE-BY-SEAT (@UC0019 @UC0020 · 409) — suất đích KHÔNG đứng tên chính (`primaryUserId` NULL: suất thứ hai của
   * một phụ huynh, suất chưa ai nhận). Guard vai đọc ghế chính chủ ⇒ vai trên ghế này không trao quyền cho ai ⇒ từ chối, 0 ghi.
   * Lối ra cho người dùng: đổi vai ở suất mà người đó ĐỨNG TÊN CHÍNH. Không `params`.
   */
  SEAT_NOT_PRIMARY: "GROUP_ROLE_SEAT_NOT_PRIMARY",
  /**
   * 🔴 ROLE-CHANGE-BY-SEAT (@UC0019 @UC0020 · 409) — `:mid` không phải id suất nào của nhóm; đường DỰ PHÒNG userId của client
   * cũ gặp userId gắn ≥2 suất ⇒ không đoán suất, 0 ghi. CÙNG mã với route xoá (`REMOVE_MEMBER_CODE.MEMBER_ID_REQUIRED`) —
   * một tình huống, một mã; câu riêng ở `ROLE_MEMBER_ID_REQUIRED_MESSAGE`.
   */
  MEMBER_ID_REQUIRED: REMOVE_MEMBER_CODE.MEMBER_ID_REQUIRED,
} as const;

/** Thông điệp lỗi (tiếng Việt sản phẩm) — một nguồn sự thật api ↔ mobile. */
export const CANNOT_CHANGE_OWNER_ROLE_MESSAGE =
  "Không thể đổi vai trò của chủ nhóm.";
export const CANNOT_CHANGE_SELF_ROLE_MESSAGE =
  "Bạn không thể tự đổi vai trò của chính mình.";
/** FIX-1278 / FIX-1278b — chỉ đường sang việc phải làm trước (§7 error model). Câu cho CẢ hạ vai / chuyển quyền / gỡ người quản lý. */
export const ROLE_CHANGE_BLOCKED_FUND_DEBT_MESSAGE =
  "Bạn còn khoản hoàn lại quỹ chưa xong (hoặc khoản chi khẩn cấp đang chờ nhóm biểu quyết). Trong lúc đó bạn chưa hạ vai hay gỡ người quản lý khác được — nâng thêm người quản lý thì vẫn được.";
/**
 * `ROLE_CHANGE_CODE.MEMBER_UNCLAIMED` — một nguồn cho CẢ cửa GUI đổi vai (`RoleManagementService`) lẫn cửa MỞ phiếu
 * biểu quyết đổi thủ quỹ (QĐ PO 27/09/2026, US-HOUSE-0001 L7b · @UC0291). Trước đó chuỗi nằm inline ở service GUI.
 */
export const MEMBER_UNCLAIMED_TREASURER_MESSAGE =
  "Không thể chỉ định thủ quỹ cho thành viên chưa có tài khoản người dùng. Hãy để họ tham gia trước.";
/** `ROLE_CHANGE_CODE.SEAT_NOT_PRIMARY` — chỉ đường sang việc làm được (§7 error model). */
export const ROLE_SEAT_NOT_PRIMARY_MESSAGE =
  "Suất này không do ai đứng tên chính nên vai trò gán vào không có tác dụng. Hãy đổi vai ở suất mà người đó đứng tên chính.";
/** `ROLE_CHANGE_CODE.MEMBER_ID_REQUIRED` ở cửa đổi vai (câu của route xoá nói "xoá"). */
export const ROLE_MEMBER_ID_REQUIRED_MESSAGE =
  "Người này gắn với nhiều suất trong nhóm — hãy cập nhật ứng dụng rồi đổi vai từ đúng suất.";

/**
 * Shape response cho POST /v1/groups/:id/members/:mid/role (đổi role thành công).
 *  - memberId: GroupMember.id của target đã đổi role.
 *  - userId: User.id ĐỨNG TÊN CHÍNH của suất đó (`primaryUserId` — luôn có: suất không đứng tên chính bị từ chối
 *    `SEAT_NOT_PRIMARY`). KHÔNG phải tham số đường dẫn.
 *  - role: vai trò SAU khi đổi (admin|treasurer|member).
 *  - replacedTreasurerUserId: khi gán treasurer mà có treasurer cũ Y bị hạ về member (atomic swap),
 *    trả userId của Y để client cập nhật badge ngay; null khi không có swap.
 * `.strict()` chặn rò field thừa (vd PII/secret) ra response (C2).
 */
export const roleChangeResponseSchema = z
  .object({
    memberId: z.string().uuid(),
    userId: z.string().uuid(),
    role: groupRoleSchema,
    replacedTreasurerUserId: z.string().uuid().nullable(),
  })
  .strict();

export type RoleChangeResponse = z.infer<typeof roleChangeResponseSchema>;

/**
 * Shared DTO cho Transfer Ownership — POST /v1/groups/:id/ownership/transfer
 * (US-APP-IMP.F1-0142 / @UC0144). Đổi CHỦ nhóm: owner hiện tại chọn 1 member active cùng nhóm làm
 * owner mới; owner cũ hạ về admin (PO chốt 06/07 — giữ owner cũ trong nhóm với quyền quản trị cao,
 * thân thiện luồng self-leave 0141). Transfer CÓ HIỆU LỰC NGAY (MVP — KHÔNG có bước accept).
 *
 * KHÁC roleChangeRequestSchema: transfer nhận `newOwnerMemberId` (GroupMember.id của owner mới),
 * KHÔNG nhận role. Endpoint riêng vì đổi owner phá one_owner nếu không swap atomic (S02 lo swap
 * Serializable + catch P2034/P2002). Một nguồn sự thật api ↔ mobile (DRY).
 *
 * Body: `{ newOwnerMemberId }` — `.strict()` chặn field lạ (C5 chống over-post: KHÔNG nhận
 * role/userId/status/id; server quyết actor từ token, target từ newOwnerMemberId).
 */
export const transferOwnershipRequestSchema = z
  .object({
    newOwnerMemberId: z.string().uuid(),
  })
  .strict();

export type TransferOwnershipRequest = z.infer<
  typeof transferOwnershipRequestSchema
>;

/**
 * Response transfer thành công:
 *  - groupId: nhóm đã đổi chủ.
 *  - newOwnerMemberId: GroupMember.id owner mới (đã là 'owner').
 *  - previousOwnerMemberId: GroupMember.id owner cũ (actor, đã hạ).
 *  - previousOwnerNewRole: role owner cũ SAU khi hạ (='admin' theo PO chốt).
 * `.strict()` chặn rò field thừa ra response (C2).
 */
export const transferOwnershipResponseSchema = z
  .object({
    groupId: z.string().uuid(),
    newOwnerMemberId: z.string().uuid(),
    previousOwnerMemberId: z.string().uuid(),
    previousOwnerNewRole: groupRoleSchema,
  })
  .strict();

export type TransferOwnershipResponse = z.infer<
  typeof transferOwnershipResponseSchema
>;

/**
 * Mã lỗi máy-đọc-được cho transfer ownership (RFC7807 `code`) — client discriminate theo code.
 */
export const TRANSFER_OWNERSHIP_CODE = {
  /** Target = chính owner đang gọi (self) → không thể chuyển cho chính mình. */
  CANNOT_TRANSFER_TO_SELF: "GROUP_OWNERSHIP_CANNOT_TRANSFER_TO_SELF",
  /**
   * 🔴 US-E24.F3-0352 HIGH-D(2) — target là ghế KHÔNG AI đăng nhập được (0 `primaryUserId` và 0
   * `MembershipAccount`) ⇒ 409. Chuyển quyền vào ghế đó = nhóm **mất owner vĩnh viễn**: owner cũ đã
   * bị hạ `admin` trong cùng thao tác, còn slot `one_owner_per_group` bị ghế chết chiếm.
   * Cùng lý lẽ với luật "thủ quỹ ma" đã có ở `role-management.service.ts` — vai điều hành phải có
   * người thật đứng sau.
   */
  TARGET_UNCLAIMED: "GROUP_OWNERSHIP_TARGET_UNCLAIMED",
} as const;

/** Thông điệp lỗi (tiếng Việt sản phẩm) — một nguồn sự thật api ↔ mobile. */
export const CANNOT_TRANSFER_TO_SELF_MESSAGE =
  "Bạn không thể chuyển quyền sở hữu cho chính mình.";
/** Chỉ đường sang việc phải làm trước, không chỉ nói cái bị cấm (§7 error model). */
export const TRANSFER_TARGET_UNCLAIMED_MESSAGE =
  "Không thể chuyển quyền sở hữu cho suất chưa có người đại diện đăng nhập được. " +
  "Hãy gắn tài khoản cho suất đó trước.";
